import { Check, Monitor, Moon, Sun } from "lucide-react"
import { useTheme, type Theme } from "@/components/theme-provider"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenuButton } from "@/components/ui/sidebar"

const MODES: { value: Theme; label: string; description: string; icon: typeof Sun }[] = [
  { value: "system", label: "Automático", description: "Segue o sistema", icon: Monitor },
  { value: "light", label: "Claro", description: "Tema claro", icon: Sun },
  { value: "dark", label: "Escuro", description: "Tema escuro", icon: Moon },
]

function ThemeIcon({ theme }: { theme: Theme }) {
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor
  return <Icon className="size-4 shrink-0" />
}

function ThemeMenuItems() {
  const { theme, setTheme } = useTheme()
  return (
    <DropdownMenuContent align="end" className="w-52">
      {MODES.map((m) => (
        <DropdownMenuItem
          key={m.value}
          onClick={() => setTheme(m.value)}
          className="flex cursor-pointer items-start gap-2 py-2"
        >
          <m.icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium leading-none">{m.label}</span>
            <span className="text-xs text-muted-foreground">{m.description}</span>
          </div>
          {theme === m.value ? (
            <Check className="mt-0.5 size-4 shrink-0 text-primary" />
          ) : (
            <span className="size-4 shrink-0" aria-hidden />
          )}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  )
}

type Props = {
  /** Compact icon button (login / setup). */
  variant?: "floating" | "sidebar"
}

export function ThemeModeSwitcher({ variant = "floating" }: Props) {
  const { theme } = useTheme()

  if (variant === "sidebar") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton tooltip="Aparência">
            <ThemeIcon theme={theme} />
            <span>Aparência</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <ThemeMenuItems />
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-9 shrink-0 rounded-full border-border/80 bg-background/80 shadow-sm backdrop-blur-sm"
          aria-label="Aparência do tema"
        >
          <ThemeIcon theme={theme} />
        </Button>
      </DropdownMenuTrigger>
      <ThemeMenuItems />
    </DropdownMenu>
  )
}
